<script setup lang="ts">
import { BaseButton } from '@/components/base'
import {
  DATE_TIME_FORMAT_24_HRS,
  DEFAULT_DATETIME_PICKER_HEIGHT,
  DISPLAY_DATE_FORMAT,
  ISO_DATE_FORMAT
} from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import { getStartOfDay } from '@/utils/helpers'
import moment from 'moment'
import { computed, nextTick, onMounted, ref, watch, watchPostEffect } from 'vue'
import FormInputField from './FormInputField.vue'

const props = defineProps<{
  noRange?: boolean
  datetime?: boolean
  color?: string
  hideLabel?: boolean
  showTime?: boolean
  disabledApply?: boolean
  disableFutureDates?: boolean
  disabled?: boolean
  startFrom?: number | string
  endAt?: number | string
  disablePastDates?: boolean
  noActions?: boolean
  loading?: boolean
  readonly?: boolean
  dateTimeFormat?: string
  multiSelect?: boolean
  returnArray?: boolean
  disableYearSelection?: boolean
  nextDatesLimit?: number
}>()
const boundValue = defineModel<any>({ default: '' })
const emit = defineEmits(['change'])
const daysInWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const monthsInYear = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const datepickerInputField = ref()
const datepickerCalendar = ref()
const isCalendarOpen = ref(false)
const currentDate = new Date(Date.now())
const showYearsList = ref(false)
const yearsList = ref<any[]>([])

const assignedDate = computed(() => {
  if (!boundValue.value) return null
  const assignedDate: string =
    Array.isArray(boundValue.value) && boundValue.value?.length > 0
      ? (boundValue.value[0] as string)
      : (boundValue.value as string)
  return new Date(assignedDate)
})

const activeMonth = ref(assignedDate.value?.getMonth() ?? currentDate.getMonth())
const activeYear = ref(assignedDate.value?.getFullYear() ?? currentDate.getFullYear())
const mouseOverTimestamp = ref()
const startTimestamp = ref()
const endTimestamp = ref()

const selectedHour = ref('')
const selectedMinute = ref('')
const hours = ref(Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')))
const minutes = ref(Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')))
const yearsToShow = 7

const computedCalendarStyle = ref({})
const positionCalendarToTopOfInput = ref(false)

const getHoursAndMinutesInMiliseconds = () => {
  const hoursToAdd = parseInt(selectedHour.value, 10)
  const minutesToAdd = parseInt(selectedMinute.value, 10)

  const toMilliseconds = (hoursToAdd * 60 * 60 + minutesToAdd * 60) * 1000
  return toMilliseconds
}

const isDateRangeSelected = computed(() => {
  if (props.disabledApply) {
    if (startTimestamp.value && endTimestamp.value) return true
    return false
  }
  return false
})

const selectedStartDate = computed(() => {
  if (!startTimestamp.value) return null
  return new Date(startTimestamp.value).getDate()
})
const selectedEndDate = computed(() => {
  if (!endTimestamp.value) return null
  return new Date(endTimestamp.value).getDate()
})

const selectedDateTimeValuesForDisplay = computed(() => {
  let formattedStartDate = ''
  let formattedEndDate = ''
  const dateFormat = props.datetime ? DATE_TIME_FORMAT_24_HRS : DISPLAY_DATE_FORMAT
  if (startTimestamp.value) {
    if (Array.isArray(boundValue.value) && boundValue.value.length == 1) {
      formattedStartDate = moment(boundValue.value[0]).format(dateFormat)
    } else {
      formattedStartDate = moment(startTimestamp.value).format(dateFormat)
    }
  }
  if (endTimestamp.value) {
    formattedEndDate = moment(endTimestamp.value).format(dateFormat)
  }
  const dateRangeText = `${formattedStartDate} ${formattedEndDate ? '-' : ''} ${formattedEndDate}`
  if (!dateRangeText.trim()) return ''
  if (!props.multiSelect) return dateRangeText
  if (!Array.isArray(boundValue.value)) return boundValue.value
  return boundValue.value.map((date: string) => moment(date).format(DISPLAY_DATE_FORMAT)).join(', ')
})

const getDayIndex = computed(() => {
  const gregorianMonths = {
    January: 13,
    February: 14,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12
  }
  const gregorianMonthIndex: number = Object.values(gregorianMonths).find(
    (value, index) => index === activeMonth.value
  ) as number
  const century = Math.floor(activeYear.value / 100)
  let yearOfCentury = activeYear.value % 100
  if (gregorianMonthIndex > 12) {
    yearOfCentury -= 1
  }
  return (dayOfMonth: number) => {
    const gregorianIndex =
      (dayOfMonth +
        Math.floor((13 * (gregorianMonthIndex + 1)) / 5) +
        yearOfCentury +
        Math.floor(yearOfCentury / 4) +
        Math.floor(century / 4) +
        5 * century) %
      7
    const index = (gregorianIndex + 5) % 7
    return index < 0 ? daysInWeek.length - 1 : index
  }
})

const updateSelectedDateTime = () => {
  let endTimestampTemp = endTimestamp.value

  if (!props.noRange && !props.showTime) endTimestampTemp += (23 * 3600 + 59 * 60) * 1000

  const newStartTimestamp = moment(startTimestamp.value).format(
    props.dateTimeFormat ?? ISO_DATE_FORMAT
  )
  if (!startTimestamp.value) {
    boundValue.value = ''
  } else if (props.multiSelect) {
    boundValue.value = Array.isArray(boundValue.value)
      ? [...boundValue.value, newStartTimestamp]
      : [newStartTimestamp]
  } else {
    boundValue.value = newStartTimestamp
    if (props.returnArray) boundValue.value = [newStartTimestamp]
  }

  emit('change')
}

const updateSelectedDateTimeAndClose = () => {
  updateSelectedDateTime()
  isCalendarOpen.value = false
}

const getNumberOfDays = (month: number) => {
  if ((month < 7 && month % 2 === 0) || (month >= 7 && month % 2 !== 0)) {
    return 31
  }
  if (month === 1) {
    if (
      activeYear.value % 4 === 0 &&
      (activeYear.value % 100 !== 0 || activeYear.value % 400 === 0)
    ) {
      return 29
    }
    return 28
  }
  return 30
}

const getDatesToDisplay = (startDayIndex: number, month: number) => {
  const datesInMonth = []
  const datesToDisplayFromPreviousMonth = () => {
    const dates = []
    let counter = startDayIndex - 1
    const previousMonth = month - 1 < 0 ? 11 : month - 1
    const numberOfDaysInPreviousMonth = getNumberOfDays(previousMonth)
    while (counter >= 0) {
      dates.push(numberOfDaysInPreviousMonth - counter)
      counter--
    }
    return dates
  }
  const datesToDisplayFromNextMonth = () => {
    const dates = []
    const activeMonthLastDate = getNumberOfDays(activeMonth.value)
    const activeMonthLastDayIndex = getDayIndex.value(activeMonthLastDate)
    const numberOfDaysToFill = daysInWeek.length - 1 - activeMonthLastDayIndex

    let counter = 1
    while (counter <= numberOfDaysToFill) {
      dates.push(counter)
      counter++
    }
    return dates
  }
  let date = 1
  while (date <= getNumberOfDays(month)) {
    datesInMonth.push(date)
    date++
  }
  return {
    previousMonthDates: datesToDisplayFromPreviousMonth(),
    currentMonthDates: datesInMonth,
    nextMonthDates: datesToDisplayFromNextMonth()
  }
}

const activeMonthDatesToDisplay = computed(() => {
  const startDayIndex = getDayIndex.value(1)
  const datesToDisplay = getDatesToDisplay(startDayIndex, activeMonth.value)
  return datesToDisplay
})

const removeDateFromSelectedDateTime = () => {
  boundValue.value = boundValue.value.filter(
    (date: string) => getStartOfDay(date).valueOf() !== startTimestamp.value
  )
}

const handleCurrentMonthDateClick = (event: Event) => {
  event.preventDefault()
  const dateElement = event.currentTarget as HTMLButtonElement
  if (!props.noRange) {
    if (endTimestamp.value) {
      startTimestamp.value = parseInt(dateElement.value, 10)
      endTimestamp.value = null
      return
    }
    if (startTimestamp.value && startTimestamp.value < dateElement.value) {
      endTimestamp.value = parseInt(dateElement.value, 10)
      return
    }
  }
  const selectedDates = boundValue.value
  startTimestamp.value = parseInt(dateElement.value, 10)
  selectedHour.value = moment(startTimestamp.value).format('HH')
  selectedMinute.value = moment(startTimestamp.value).format('mm')
  updateSelectedDateTime()
  if (Array.isArray(selectedDates)) {
    const selectedDateTimestamps = selectedDates.map((date: string) =>
      moment(date).startOf('day').valueOf()
    )
    if (selectedDateTimestamps.includes(startTimestamp.value)) {
      removeDateFromSelectedDateTime()
    }
  }
  if (props.multiSelect) return
  nextTick(() => {
    isCalendarOpen.value = false
  })
}

const updateCurrentMonthMouseOver = (event: Event) => {
  if (event instanceof MouseEvent) {
    const destination = event.relatedTarget as HTMLElement
    if (
      event.type === 'mouseleave' &&
      !destination.classList.contains('.datepicker-item') &&
      !endTimestamp.value
    ) {
      mouseOverTimestamp.value = null
      return
    }
  }
  if (selectedStartDate.value && !selectedEndDate.value) {
    const targetElement = event.currentTarget as HTMLButtonElement
    mouseOverTimestamp.value = targetElement.value
  }
}

const shouldHighlight = (date: number) => {
  if (props.noRange) {
    return ''
  }

  const dateTimestamp = new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
  if (!selectedStartDate.value) return ''

  let isDateWithinRange
  if (
    dateTimestamp >= (startTimestamp?.value || 0) &&
    dateTimestamp <= (endTimestamp?.value || Infinity) &&
    mouseOverTimestamp.value > selectedStartDate.value &&
    dateTimestamp <= (mouseOverTimestamp.value || 0)
  ) {
    isDateWithinRange = true
  }
  return isDateWithinRange ? 'highlight' : ''
}

const isSelected = (date: number) => {
  const dateTimestamp = new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
  const dateString = new Date(dateTimestamp).toLocaleDateString()
  const startDateString = new Date(startTimestamp.value).toLocaleDateString()
  const endDateString = new Date(endTimestamp.value).toLocaleDateString()

  if (props.multiSelect && Array.isArray(boundValue.value)) {
    const selectedDates = boundValue.value.map((date: string) =>
      moment(date).startOf('day').valueOf()
    )
    return selectedDates.includes(dateTimestamp) ? 'selected no-range' : ''
  }

  if (dateString === startDateString) {
    return props.noRange ? 'selected no-range' : 'selected selected-start'
  }

  if (
    dateString === endDateString ||
    (dateTimestamp === mouseOverTimestamp.value &&
      dateTimestamp > startTimestamp.value &&
      !endTimestamp.value)
  ) {
    return 'selected selected-end'
  }
  return ''
}

const showPreviousMonth = () => {
  if (activeMonth.value === 0) {
    activeMonth.value = 11
    activeYear.value--
    return
  }
  activeMonth.value--
}

const showNextMonth = () => {
  if (activeMonth.value === 11) {
    activeMonth.value = 0
    activeYear.value++
    return
  }
  activeMonth.value++
}

const resetDatepicker = () => {
  startTimestamp.value = null
  endTimestamp.value = null

  if (props.noRange) updateSelectedDateTime()
}

const setStartAndEndDates = () => {
  if (boundValue.value) {
    startTimestamp.value = boundValue.value
    selectedHour.value = moment(startTimestamp.value).format('HH')
    selectedMinute.value = moment(startTimestamp.value).format('mm')
  }
}

const handleCalendarVisibility = () => {
  if (props.readonly || props.disabled) return
  isCalendarOpen.value = true
}

const setActiveYear = (year: number) => {
  activeYear.value = year
  showYearsList.value = false
}

const showPreviousYears = (years: number) => {
  const currentFirstYear = yearsList.value[0]
  const newYearsList = []
  for (let i = currentFirstYear - years; i <= currentFirstYear - 1; i++) {
    newYearsList.push(i)
  }
  yearsList.value = newYearsList
}

const showNextYears = (years: number) => {
  const currentLastYear = yearsList.value[yearsList.value.length - 1]
  const newYearsList = []
  for (let i = currentLastYear + 1; i <= currentLastYear + years; i++) {
    newYearsList.push(i)
  }
  yearsList.value = newYearsList
}

watch(
  () => props.startFrom,
  () => {
    if (!props.startFrom || !startTimestamp.value) return
    const startFromDate = new Date(props.startFrom)
    const startFromTimestamp = startFromDate.getTime()
    const currentStartTimestamp = startTimestamp.value
      ? getStartOfDay(startTimestamp.value).valueOf()
      : 0
    if (!startTimestamp.value || startFromTimestamp > currentStartTimestamp) {
      startTimestamp.value = startFromTimestamp
    } else if (startTimestamp.value && startFromTimestamp < startTimestamp.value) {
      return
    }
    activeMonth.value = startFromDate.getMonth()
    activeYear.value = startFromDate.getFullYear()
    updateSelectedDateTime()
  }
)

watchPostEffect(() => {
  const years = []
  const yearsCount = parseInt((yearsToShow / 2).toString(), 10)
  for (let i = activeYear.value - yearsCount; i <= activeYear.value + yearsCount; i++) {
    years.push(i)
  }
  yearsList.value = years
})

watch(isCalendarOpen, () => {
  nextTick(() => {
    const inputFieldElement = datepickerInputField.value.$el
    const calendarElementHeight = datepickerCalendar.value?.$el.clientHeight

    const { top, bottom, left } = inputFieldElement.getBoundingClientRect()
    const inputFieldElementHeight = bottom - top

    const distanceFromWindowBottom = window.innerHeight - top

    let calendarPosition: any = {
      zIndex: 9999,
      left: `${left}px`
    }

    if (distanceFromWindowBottom < calendarElementHeight + inputFieldElementHeight) {
      positionCalendarToTopOfInput.value = true
      calendarPosition = {
        ...calendarPosition,
        top: `${top - calendarElementHeight}px`
      }
    } else {
      positionCalendarToTopOfInput.value = false
      calendarPosition = {
        ...calendarPosition,
        top: `${bottom}px`
      }
    }
    computedCalendarStyle.value = calendarPosition
  })
})

watchPostEffect(() => {
  setStartAndEndDates()
})

onMounted(() => {
  setStartAndEndDates()
})

defineExpose({ resetDatepicker })

const updateTimeStamp = () => {
  const timeStampWithDateTime = startTimestamp.value

  const dateWithTime = new Date(timeStampWithDateTime)
  dateWithTime.setHours(0, 0, 0, 0)
  const timeStampWithoutTime = dateWithTime.getTime()

  startTimestamp.value = 0
  startTimestamp.value = timeStampWithoutTime + getHoursAndMinutesInMiliseconds()

  if (props.noRange) updateSelectedDateTime()
}

const isPastDate = (date: number) => {
  if (props.disablePastDates) {
    const cellTimestamp = new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
    const lastActiveDateTimestamp = props.startFrom
      ? new Date(props.startFrom).getTime()
      : currentDate.getTime()
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayLastActiveDate = getStartOfDay(lastActiveDateTimestamp)
    if (startOfDayCellDate < startOfDayLastActiveDate) {
      return 'disabled'
    }
  }
  return ''
}

const isFutureDate = (date: any) => {
  if (props.disableFutureDates) {
    const cellTimestamp = new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
    const lastActiveDateTimestamp = props.endAt
      ? new Date(props.endAt).getTime()
      : currentDate.getTime()
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayLastActiveDate = getStartOfDay(lastActiveDateTimestamp)

    if (startOfDayCellDate > startOfDayLastActiveDate) {
      return 'disabled'
    }
  }
  return ''
}

const isInNextDatesLimit = (date: any) => {
  if (props.nextDatesLimit) {
    const cellTimestamp = new Date(`${activeMonth.value + 1}/${date}/${activeYear.value}`).getTime()
    const nextDatesLimitTimestamp =
      currentDate.getTime() + props.nextDatesLimit * 24 * 60 * 60 * 1000
    const startOfDayCellDate = getStartOfDay(cellTimestamp)
    const startOfDayNextDatesLimit = getStartOfDay(nextDatesLimitTimestamp)

    if (startOfDayCellDate > startOfDayNextDatesLimit) {
      return 'disabled'
    }
  }
  return ''
}
</script>

<template>
  <div class="datepicker form-datepicker">
    <FormInputField
      ref="datepickerInputField"
      v-model="selectedDateTimeValuesForDisplay"
      readonly
      :height="$attrs.height ?? DEFAULT_DATETIME_PICKER_HEIGHT"
      :class="`datetime-input ${$attrs.small !== undefined ? 'body-3 medium' : 'inherit--font'}`"
      :show-highlight="Boolean(selectedDateTimeValuesForDisplay)"
      :disabled="disabled || loading"
      :loading="loading"
      v-bind="$attrs"
      @click="handleCalendarVisibility"
    >
      <template #append-inner>
        <v-icon
          v-if="readonly || (startTimestamp == null && endTimestamp == null)"
          icon="mdi-calendar"
          size="26"
        />
        <v-icon v-else icon="mdi-close" size="20" @click.stop="resetDatepicker" />
      </template>
    </FormInputField>
    <Transition
      :name="positionCalendarToTopOfInput ? 'slide-y-transition' : 'slide-y-reverse-transition'"
    >
      <Teleport to="body">
        <v-card
          v-if="isCalendarOpen"
          ref="datepickerCalendar"
          id="datepicker-calendar"
          :right="$attrs['align-calendar'] === 'right'"
          class="calendar--active position-absolute"
          :style="computedCalendarStyle"
          max-width="340"
          v-click-outside="() => (isCalendarOpen = false)"
        >
          <v-card-title class="d-flex justify-space-between black--text body-1 semibold py-4">
            <v-icon @click="showPreviousMonth" @focusin.stop icon="mdi:mdi-chevron-left" />
            {{ monthsInYear[activeMonth] }}
            <span
              v-if="!disableYearSelection"
              @click="showYearsList = true"
              class="cursor-pointer year-picker-year"
            >
              {{ activeYear }}
            </span>
            <v-icon @click="showNextMonth" @focusin.stop icon="mdi:mdi-chevron-right" />
          </v-card-title>
          <v-card-subtitle class="d-flex weekday-labels bg-slate_100 text-slate_400 medium">
            <span
              v-for="(day, index) in daysInWeek"
              :key="index"
              class="datepicker-item text-center body-1--new"
              >{{ day }}</span
            >
          </v-card-subtitle>
          <v-card-text
            v-if="showYearsList"
            class="datepicker-content d-flex flex-wrap flex-center"
            style="justify-content: space-evenly"
          >
            <div @click="showPreviousYears(yearsToShow)" class="datepicker-year-nav-button">
              Prev
            </div>
            <div class="d-flex flex-column">
              <div
                v-for="(year, index) in yearsList"
                :key="index"
                class="datepicker-item d-inline-block text-center body-2 regular black--text py-1 year-picker-year cursor-pointer"
                @click="setActiveYear(year)"
              >
                {{ year }}
              </div>
            </div>
            <div @click="showNextYears(yearsToShow)" class="datepicker-year-nav-button">Next</div>
          </v-card-text>
          <v-card-text v-else class="datepicker-content d-flex flex-wrap">
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.previousMonthDates"
              :key="`previous-month-${index}`"
              class="datepicker-item d-inline-block text-center body-2 regular black--text disabled"
            >
              {{ date }}
            </button>
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.currentMonthDates"
              :key="index"
              :class="`
                ${isSelected(date)}
                ${shouldHighlight(date)}
                ${isPastDate(date)}
                ${isFutureDate(date)}
                ${isInNextDatesLimit(date)}
                datepicker-item d-inline-block text-center body-2 regular black--text
              `"
              :value="new Date(`${activeMonth + 1}/${date}/${activeYear}`).getTime()"
              :today="
                date === currentDate.getDate() &&
                activeMonth === currentDate.getMonth() &&
                activeYear === currentDate.getFullYear()
              "
              @click.stop="handleCurrentMonthDateClick"
              @mouseenter.stop="updateCurrentMonthMouseOver"
              @mouseleave.stop="updateCurrentMonthMouseOver"
              @focusin.stop="updateCurrentMonthMouseOver"
              @focusout.stop="updateCurrentMonthMouseOver"
            >
              <span class="date-wrapper">{{ date }}</span>
            </button>
            <button
              v-for="(date, index) in activeMonthDatesToDisplay.nextMonthDates"
              :key="`next-month-${index}`"
              class="datepicker-item d-inline-block text-center body-2 regular black--text disabled"
            >
              {{ date }}
            </button>
          </v-card-text>
          <v-card-actions v-if="false" class="d-flex pa-5">
            <BaseButton
              height="40"
              min-width="unset"
              class="black--text text-capitalize btn-bottom"
              @click="resetDatepicker"
            >
              {{ CONSTANTS.RESET }}
            </BaseButton>
            <BaseButton
              height="40"
              class="body-1 text-capitalize white--text brand_light flex-grow-1"
              @click="updateSelectedDateTimeAndClose"
              :disabled="disabledApply ? !isDateRangeSelected : false"
            >
              {{ CONSTANTS.APPLY }}
            </BaseButton>
          </v-card-actions>
        </v-card>
      </Teleport>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.datepicker-year-nav-button {
  cursor: pointer;
  padding: 4px 20px;
  border: 1px solid #e0e0e0;
  height: 30px;
  user-select: none;
}
.year-picker-year:hover {
  color: rgb(var(--v-theme-blue_600));
}
</style>
